import { useChat } from '@ai-sdk/react';
import { getApiEndpoint } from '../../../config/api.js';
import type { Widget } from '../types/index.js';
import type { UIMessage, CanvasContext } from '../types/messages.js';

interface UseAgentChatOptions {
  canvasId?: string;
  widgets: Widget[];
  connections: any[];
  streaming?: boolean;
  discoveryState?: any;
}

export function useAgentChat({ 
  canvasId, 
  widgets, 
  connections, 
  streaming = false, 
  discoveryState 
}: UseAgentChatOptions) {
  const apiPath = streaming ? `${getApiEndpoint()}/agent/stream` : `${getApiEndpoint()}/agent`;

  // Build canvas context for the body
  const canvasState: CanvasContext = {
    canvasId,
    widgets: widgets.map((w) => ({
      id: w.id,
      type: w.type,
      title: w.config.title,
      position: w.position,
      outputs: w.outputs,
    })),
    connections,
    discoveryState,
  };

  // Configure chat options for @ai-sdk/react v2
  const chatOptions: any = {
    // API endpoint configuration
    api: apiPath,
    headers: {
      'Content-Type': 'application/json',
    },
    body: {
      canvasState,
      temperature: 0.7,
    },
    // Streaming configuration: default to UIMessage SSE (AI SDK v5)
    // Do not force 'text' protocol; server returns UIMessage stream
    // Error handling
    onError: (error: Error) => {
      console.error('Chat error:', error);
    },
    // Completion handling
    onFinish: (message: any) => {
      console.log('Chat finished:', message);
    },
  };

  // Use the v2 useChat hook
  const chatHelpers = useChat(chatOptions);

  // Map messages to include content property for backward compatibility
  const messagesWithContent = chatHelpers.messages.map((m: any) => ({
    ...m,
    content: m.content || (m.parts?.filter((p: any) => p.type === 'text')
      .map((p: any) => p.text || '').join('')) || ''
  }));

  // Return the chat helpers with proper typing and compatibility layer
  return {
    messages: messagesWithContent,
    sendMessage: chatHelpers.sendMessage,
    regenerate: chatHelpers.regenerate,
    stop: chatHelpers.stop,
    status: chatHelpers.status,
    error: chatHelpers.error,
    setMessages: chatHelpers.setMessages,
    clearError: chatHelpers.clearError || (() => {}),
    id: chatHelpers.id,
    // Add input handling for compatibility
    input: '',
    handleInputChange: () => {},
    handleSubmit: (e: any) => { e?.preventDefault(); },
    // Add convenience methods for compatibility
    isLoading: chatHelpers.status === 'streaming' || chatHelpers.status === 'submitted',
    canRegenerate: chatHelpers.messages.length > 0,
  };
}

// Export type for components using this hook
export type AgentChatHelpers = ReturnType<typeof useAgentChat>;
